import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Pencil, Trash2, MapPin, Phone, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const emptyForm = { name: "", address: "", city: "", state: "", zip_code: "", phone: "" };

export default function Locations() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const canEdit = ["owner", "manager", "admin"].includes(user?.role);
  const canDelete = ["owner", "admin"].includes(user?.role);

  const fetchLocations = async () => {
    try {
      const { data } = await API.get("/management/locations");
      setLocations(data.locations);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLocations(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (loc) => {
    setEditing(loc.id);
    setForm({ name: loc.name, address: loc.address, city: loc.city, state: loc.state, zip_code: loc.zip_code, phone: loc.phone });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error("Location name is required"); return; }
    setSubmitting(true);
    try {
      if (editing) {
        await API.put(`/management/locations/${editing}`, form);
        toast.success("Location updated");
      } else {
        await API.post("/management/locations", form);
        toast.success("Location created");
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/management/locations/${loc.id}`);
      toast.success("Location deleted");
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="locations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">Locations / Offices</h1>
          <p className="text-sm text-zinc-500">{locations.length} locations</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="add-location-button">
            <Plus className="w-4 h-4 mr-2" /> Add Location
          </Button>
        )}
      </div>

      {locations.length === 0 ? (
        <Card className="border border-zinc-200">
          <CardContent className="p-8 text-center">
            <Building2 className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No locations yet</p>
            <p className="text-xs text-zinc-400 mt-1">Add your first office or branch location</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Card view for mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:hidden">
            {locations.map(loc => (
              <Card key={loc.id} className="border border-zinc-200 card-hover" data-testid={`location-card-${loc.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900">{loc.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                        <MapPin className="w-3 h-3" />
                        <span>{loc.city}, {loc.state} {loc.zip_code}</span>
                      </div>
                    </div>
                    <Badge className={loc.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                      {loc.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {loc.address && <p className="text-xs text-zinc-500 mb-1">{loc.address}</p>}
                  {loc.phone && (
                    <div className="flex items-center gap-1 text-xs text-zinc-500 mb-2">
                      <Phone className="w-3 h-3" /> {loc.phone}
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-xs text-zinc-500 mb-3">
                    <Users className="w-3 h-3" /> {loc.technician_count || 0} technicians assigned
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openEdit(loc)}
                        data-testid={`edit-location-${loc.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      {canDelete && (
                        <Button variant="outline" size="sm" className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                          onClick={() => handleDelete(loc)} data-testid={`delete-location-${loc.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Table view for desktop */}
          <Card className="border border-zinc-200 hidden md:block">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Location</TableHead>
                    <TableHead className="text-xs">City/State</TableHead>
                    <TableHead className="text-xs">Phone</TableHead>
                    <TableHead className="text-xs">Technicians</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    {canEdit && <TableHead className="text-xs">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locations.map(loc => (
                    <TableRow key={loc.id} data-testid={`location-row-${loc.id}`}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium text-zinc-900">{loc.name}</p>
                          <p className="text-xs text-zinc-400">{loc.address}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-zinc-600">{loc.city}, {loc.state} {loc.zip_code}</TableCell>
                      <TableCell className="text-sm text-zinc-600">{loc.phone || "\u2014"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-zinc-400" />
                          <span className="text-sm">{loc.technician_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${loc.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                          {loc.is_active ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="text-xs" onClick={() => openEdit(loc)}
                              data-testid={`edit-location-${loc.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            {canDelete && (
                              <Button variant="ghost" size="sm" className="text-xs text-rose-600 hover:bg-rose-50"
                                onClick={() => handleDelete(loc)} data-testid={`delete-location-${loc.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Location" : "Add New Location"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Location Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Dallas HQ" className="mt-1" data-testid="location-name-input" />
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                placeholder="Street address" className="mt-1" data-testid="location-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                  className="mt-1" data-testid="location-city-input" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))}
                  placeholder="TX" className="mt-1" data-testid="location-state-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">ZIP Code</Label>
                <Input value={form.zip_code} onChange={e => setForm(p => ({ ...p, zip_code: e.target.value }))}
                  className="mt-1" data-testid="location-zip-input" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="(555) 000-0000" className="mt-1" data-testid="location-phone-input" />
              </div>
            </div>
            <Button onClick={handleSubmit} disabled={submitting || !form.name.trim()}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="submit-location-button">
              {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : editing ? "Update Location" : "Create Location"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
